import {
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { CircleRemoveIcon, ReorderIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Checkbox,
	FlexGap,
	IconButton,
	Input,
	Spinner,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import NotePanel from './NotePanel';
import NoteTaskLineReadOnly from './NoteTaskLineReadOnly';

const DragHandle = styled.button`
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	padding: 0;
	cursor: grab;
	color: ${tokens.icon.secondary};
	touch-action: none;

	&:active {
		cursor: grabbing;
	}

	svg {
		width: 16px;
		height: 16px;

		path {
			fill: ${tokens.icon.secondary};
		}
	}
`;

const TitleRead = styled.button`
	flex: 1;
	min-width: 0;
	border: none;
	background: transparent;
	text-align: left;
	font-size: ${tokens.typography.sizes.h3}px;
	color: ${tokens.text.primary};
	font-family: inherit;
	padding: ${tokens.gap.inline}px 0;
	cursor: text;

	&:hover {
		background: ${tokens.surface.hover};
		border-radius: ${tokens.radius.xs}px;
	}
`;

type TaskItem = graphql.NoteTaskFragment;

function TaskLine(props: {
	task: TaskItem;
	togglePending: boolean;
	deletePending: boolean;
	onToggle: (id: string, next: graphql.TaskStatus) => Promise<void>;
	onRename: (id: string, title: string) => Promise<void>;
	onDelete: (id: string) => Promise<void>;
}) {
	const { task, togglePending, deletePending, onToggle, onRename, onDelete } = props;
	const { t } = useTranslation();
	const [title, setTitle] = useState(task.title);
	const [editingTitle, setEditingTitle] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: task.id,
		animateLayoutChanges: () => false,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
	};

	useEffect(() => {
		setTitle(task.title);
	}, [task.id, task.title]);

	useLayoutEffect(() => {
		if (editingTitle) {
			titleInputRef.current?.focus();
			titleInputRef.current?.select();
		}
	}, [editingTitle]);

	const saveTitle = useCallback(async () => {
		const next = title.trim();
		if (!next || next === task.title) {
			setTitle(task.title);
			return;
		}
		await onRename(task.id, next);
	}, [onRename, task.id, task.title, title]);

	const isDone = task.status === graphql.TaskStatus.Done;

	return (
		<FlexGap ref={setNodeRef} style={style} mb={2} alignItems="center" gap={8}>
			<DragHandle
				type="button"
				{...attributes}
				{...listeners}
				aria-label={t('oneOnOne.tasks.reorder')}
			>
				<ReorderIcon aria-hidden />
			</DragHandle>

			<Checkbox
				mr={0}
				checked={isDone}
				disabled={togglePending}
				onClick={() =>
					void onToggle(task.id, isDone ? graphql.TaskStatus.Todo : graphql.TaskStatus.Done)
				}
				aria-label={isDone ? t('oneOnOne.tasks.markTodo') : t('oneOnOne.tasks.markDone')}
			/>

			{editingTitle ? (
				<Input
					inputRef={titleInputRef}
					value={title}
					small
					onChange={(value) => setTitle(value)}
					onBlur={() => {
						void saveTitle().finally(() => setEditingTitle(false));
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							void saveTitle().finally(() => setEditingTitle(false));
						}
						if (e.key === 'Escape') {
							setTitle(task.title);
							setEditingTitle(false);
						}
					}}
				/>
			) : (
				<TitleRead type="button" onClick={() => setEditingTitle(true)}>
					{task.title}
				</TitleRead>
			)}
			<IconButton
				type="plain"
				icon={<CircleRemoveIcon aria-hidden />}
				loading={deletePending}
				onClick={() => void onDelete(task.id)}
				aria-label={t('oneOnOne.tasks.delete')}
			/>
		</FlexGap>
	);
}

export default function NoteTasksPanel(props: {
	noteId: string;
	tasks: readonly TaskItem[];
	onRefetch: () => Promise<unknown>;
	readOnly?: boolean;
}) {
	const { noteId, tasks, onRefetch, readOnly = false } = props;
	const { t } = useTranslation();
	const [newTitle, setNewTitle] = useState('');
	const [pendingToggleId, setPendingToggleId] = useState<string | null>(null);
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
	const [creatingTask, setCreatingTask] = useState(false);
	const [createTask] = graphql.useCreateTaskMutation();
	const [updateTask] = graphql.useUpdateTaskMutation();
	const [deleteTask] = graphql.useDeleteTaskMutation();
	const [reorderTask] = graphql.useReorderTaskMutation();

	const sortedTasks = useMemo(() => {
		const seen = new Set<string>();
		return [...tasks]
			.filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)))
			.sort((a, b) => a.order - b.order);
	}, [tasks]);

	const [orderedTasks, setOrderedTasks] = useState<readonly TaskItem[]>(sortedTasks);

	useEffect(() => {
		setOrderedTasks(sortedTasks);
	}, [sortedTasks]);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const refetch = useCallback(async () => {
		await onRefetch();
	}, [onRefetch]);

	const handleCreate = useCallback(async () => {
		const title = newTitle.trim();
		if (!title) {
			return;
		}
		setCreatingTask(true);
		try {
			await createTask({
				variables: {
					input: {
						title,
						subjectID: noteId,
						subjectType: graphql.TaskSubjectType.Note,
					},
				},
			});
			setNewTitle('');
			await refetch();
		} finally {
			setCreatingTask(false);
		}
	}, [createTask, newTitle, noteId, refetch]);

	const handleToggle = useCallback(
		async (id: string, status: graphql.TaskStatus) => {
			setPendingToggleId(id);
			try {
				await updateTask({
					variables: { input: { id, status } },
				});
				await refetch();
			} finally {
				setPendingToggleId(null);
			}
		},
		[refetch, updateTask],
	);

	const handleRename = useCallback(
		async (id: string, title: string) => {
			await updateTask({
				variables: { input: { id, title } },
			});
			await refetch();
		},
		[refetch, updateTask],
	);

	const handleDelete = useCallback(
		async (id: string) => {
			setPendingDeleteId(id);
			try {
				await deleteTask({
					variables: { input: { id } },
				});
				await refetch();
			} finally {
				setPendingDeleteId((current) => (current === id ? null : current));
			}
		},
		[deleteTask, refetch],
	);

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) {
				return;
			}
			const oldIndex = orderedTasks.findIndex((task) => task.id === active.id);
			const newIndex = orderedTasks.findIndex((task) => task.id === over.id);
			if (oldIndex < 0 || newIndex < 0) {
				return;
			}

			const reordered = arrayMove([...orderedTasks], oldIndex, newIndex);
			const prev = reordered[newIndex - 1];
			const next = reordered[newIndex + 1];

			let newOrder: number;
			if (!prev && next) {
				newOrder = next.order - 1000;
			} else if (prev && !next) {
				newOrder = prev.order + 1000;
			} else if (prev && next) {
				newOrder = (prev.order + next.order) / 2;
			} else {
				newOrder = Date.now();
			}

			setOrderedTasks(
				reordered.map((task) => (task.id === active.id ? { ...task, order: newOrder } : task)),
			);

			try {
				await reorderTask({
					variables: { input: { id: String(active.id), order: newOrder } },
				});
				await refetch();
			} catch {
				setOrderedTasks(sortedTasks);
			}
		},
		[orderedTasks, refetch, reorderTask, sortedTasks],
	);

	const renderTaskLine = (task: TaskItem) => (
		<TaskLine
			key={task.id}
			task={task}
			togglePending={pendingToggleId === task.id}
			deletePending={pendingDeleteId === task.id}
			onToggle={handleToggle}
			onRename={handleRename}
			onDelete={handleDelete}
		/>
	);

	return (
		<NotePanel>
			<Subheading>{t('oneOnOne.tasks.panelTitle')}</Subheading>
			<Box>
				{readOnly ? (
					orderedTasks.map((task) => <NoteTaskLineReadOnly key={task.id} task={task} />)
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						modifiers={[restrictToVerticalAxis]}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={orderedTasks.map((task) => task.id)}
							strategy={verticalListSortingStrategy}
						>
							{orderedTasks.map(renderTaskLine)}
						</SortableContext>
					</DndContext>
				)}
			</Box>
			{!readOnly && (
				<FlexGap alignItems="center" gap={8}>
					<Input
						flexGrow={1}
						value={newTitle}
						small
						disabled={creatingTask}
						onChange={(value) => setNewTitle(value)}
						placeholder={t('oneOnOne.tasks.addPlaceholder')}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								void handleCreate();
							}
						}}
					/>
					{creatingTask ? <Spinner /> : null}
				</FlexGap>
			)}
		</NotePanel>
	);
}
